import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useModalState } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useState } from "react";

import { HolidayGiftBanner } from "./Banner";
import { HolidayGiftBottomSheet } from "./BottomSheet";
import { HolidayGiftInstructionsBottomSheet } from "./Instructions";
import { useShareHolidayGift } from "./useShareHolidayGift";

const MODAL_TRANSITION_DELAY_IN_MS = 50;
const HOLIDAY_GIFT_SCORE_THRESHOLD = 1000;

export function HolidayGiftEntrypoint() {
  const bottomSheetModalState = useModalState();
  const instructionsModalState = useModalState();
  const [showConfetti, setShowConfetti] = useState<boolean>(true);

  const { userId, email } = useDefinedWorker();
  const { data: { clipboardScore = 0 } = {} } = useGetClipboardScoreProfile(userId);

  const { mutateAsync: shareHolidayGift, isLoading: isSharing } = useShareHolidayGift();

  const isHolidayGiftEnabled = useCbhFlag(CbhFeatureFlag.HOLIDAY_GIFT, {
    defaultValue: false,
  });

  const showHolidayGiftBanner =
    isHolidayGiftEnabled && clipboardScore >= HOLIDAY_GIFT_SCORE_THRESHOLD;

  useLogEffect(
    APP_V2_USER_EVENTS.HOLIDAY_GIFT_BANNER_SHOWN,
    { workerId: userId },
    { enabled: showHolidayGiftBanner }
  );

  if (!showHolidayGiftBanner) {
    return null;
  }

  const onBannerCtaClick = () => {
    bottomSheetModalState.openModal();
    logEvent(APP_V2_USER_EVENTS.HOLIDAY_GIFT_BANNER_CTA_CLICKED, { workerId: userId });
  };

  const onViewRedemptionInstructionsClick = () => {
    instructionsModalState.openModal();
    setShowConfetti(false);

    // Close the previous modal with a little delay to prevent the underlying page from being visible during the transition
    setTimeout(() => {
      bottomSheetModalState.closeModal();
    }, MODAL_TRANSITION_DELAY_IN_MS);
    logEvent(APP_V2_USER_EVENTS.HOLIDAY_GIFT_INSTRUCTIONS_CTA_CLICKED, { workerId: userId });
  };

  const handleShare = async () => {
    logEvent(APP_V2_USER_EVENTS.HOLIDAY_GIFT_SHARE_CTA_CLICKED, { workerId: userId });

    const { shared, error } = await shareHolidayGift();

    if (shared) {
      logEvent(APP_V2_APP_EVENTS.HOLIDAY_GIFT_SHARE_SUCCESS, {
        workerId: userId,
      });
    } else {
      logEvent(APP_V2_APP_EVENTS.HOLIDAY_GIFT_SHARE_FAILURE, {
        workerId: userId,
        error,
      });
    }
  };

  return (
    <>
      <HolidayGiftBanner onClick={onBannerCtaClick} />
      <HolidayGiftBottomSheet
        modalState={bottomSheetModalState}
        showConfetti={showConfetti}
        isSharing={isSharing}
        handleShare={handleShare}
        onViewRedemptionInstructionsClick={onViewRedemptionInstructionsClick}
      />
      <HolidayGiftInstructionsBottomSheet
        modalState={instructionsModalState}
        userEmail={email}
        onClose={() => {
          instructionsModalState.closeModal();
        }}
        onBack={() => {
          bottomSheetModalState.openModal();

          // Close the previous modal with a little delay to prevent the underlying page from being visible during the transition
          setTimeout(() => {
            instructionsModalState.closeModal();
          }, MODAL_TRANSITION_DELAY_IN_MS);
        }}
      />
    </>
  );
}
